import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Save, User, Calendar, DollarSign } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import { payrollAPI, employeesAPI } from "@/lib/api";

export default function PayslipGenerator({ onClose, onGenerated }) {
  const { t } = useTranslation();
  const { toast, success: showSuccess, error: showError } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [formData, setFormData] = useState({
    employe_id: "",
    periode_debut: "",
    periode_fin: "",
    salaire_base: "",
    heures_travaillees: 173.33, // Moroccan standard (40h/semaine)
    heures_supplementaires: 0,
    taux_heures_sup: 1.25,
    primes: 0,
    nombre_personnes_charge: 0,
    autres_deductions: 0,
  });

  const [calculations, setCalculations] = useState({
    salaire_brut: 0,
    cotisations_cnss: 0,
    cotisations_amo: 0,
    cotisations_retraite: 0,
    cotisations_sociales: 0,
    frais_professionnels: 0,
    salaire_net_imposable: 0,
    ir_brut: 0,
    deduction_personnes: 0,
    ir_net: 0,
    salaire_net: 0,
  });

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployee) {
      setFormData((prev) => ({
        ...prev,
        salaire_base: selectedEmployee.salaire || "",
      }));
    }
  }, [selectedEmployee]);

  useEffect(() => {
    calculateSalary();
  }, [formData]);

  const loadEmployees = async () => {
    try {
      const response = await employeesAPI.getAll({ limit: 1000 });
      setEmployees(response.data?.data?.employees || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const calculateSalary = () => {
    const salaireBase = parseFloat(formData.salaire_base) || 0;
    const heuresSupplementaires =
      parseFloat(formData.heures_supplementaires) || 0;
    const tauxHeuresSup = parseFloat(formData.taux_heures_sup) || 1.25;
    const primes = parseFloat(formData.primes) || 0;
    const nombrePersonnesCharge =
      parseInt(formData.nombre_personnes_charge) || 0;
    const autresDeductions = parseFloat(formData.autres_deductions) || 0;
    const heuresTravaillees = parseFloat(formData.heures_travaillees) || 173.33;

    // 1. Calculer le salaire brut selon la formule exacte
    const tauxHoraire = salaireBase / heuresTravaillees;
    const paieHeuresSupplementaires =
      heuresSupplementaires * tauxHoraire * tauxHeuresSup;
    const salaireBrut = salaireBase + paieHeuresSupplementaires + primes;

    // 2. Calculer les frais professionnels (20% plafonné à 2500 MAD)
    const fraisProfessionnels = Math.min(salaireBrut * 0.2, 2500);

    // 3. Calculer les cotisations sociales
    const baseCNSS = Math.min(salaireBrut, 6000); // Plafond CNSS
    const cotisationsCNSS = baseCNSS * 0.0429; // 4.29%
    const cotisationsAMO = salaireBrut * 0.0226; // 2.26%
    const cotisationsRetraite = 0; // 0% (ajustable)
    const totalCotisations =
      cotisationsCNSS + cotisationsAMO + cotisationsRetraite;

    // 4. Calculer le salaire net imposable
    const salaireNetImposable =
      salaireBrut - fraisProfessionnels - totalCotisations;

    // 5. Calculer l'IR selon le barème marocain exact
    const irBrut = calculateMoroccanIncomeTax(
      salaireNetImposable,
      nombrePersonnesCharge
    );

    // 6. Calculer le salaire net final
    const salaireNet =
      salaireBrut - totalCotisations - irBrut - autresDeductions;

    setCalculations({
      salaire_brut: salaireBrut,
      cotisations_cnss: cotisationsCNSS,
      cotisations_amo: cotisationsAMO,
      cotisations_retraite: cotisationsRetraite,
      cotisations_sociales: totalCotisations,
      frais_professionnels: fraisProfessionnels,
      salaire_net_imposable: salaireNetImposable,
      ir_brut: irBrut,
      deduction_personnes: Math.min(nombrePersonnesCharge, 6) * 30, // 30 MAD/mois par personne
      ir_net: irBrut,
      salaire_net: salaireNet,
    });
  };

  // Calcul de l'IR selon le barème marocain exact
  const calculateMoroccanIncomeTax = (
    salaireNetImposable,
    nombrePersonnesCharge
  ) => {
    // Convertir en revenu annuel
    const revenuAnnuel = salaireNetImposable * 12;

    // Déduction pour personnes à charge (360 MAD/an par personne, max 6)
    const deductionPersonnes = Math.min(nombrePersonnesCharge, 6) * 360;
    const revenuImposable = Math.max(revenuAnnuel - deductionPersonnes, 0);

    let irAnnuel = 0;

    // Barème progressif exact
    if (revenuImposable <= 30000) {
      irAnnuel = 0;
    } else if (revenuImposable <= 50000) {
      irAnnuel = revenuImposable * 0.1 - 3000;
    } else if (revenuImposable <= 60000) {
      irAnnuel = revenuImposable * 0.2 - 8000;
    } else if (revenuImposable <= 80000) {
      irAnnuel = revenuImposable * 0.3 - 14000;
    } else if (revenuImposable <= 180000) {
      irAnnuel = revenuImposable * 0.34 - 17000;
    } else {
      irAnnuel = revenuImposable * 0.38 - 24400;
    }

    // Convertir en mensuel et s'assurer qu'il n'est pas négatif
    return Math.max(0, irAnnuel / 12);
  };

  const handleEmployeeChange = (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    setSelectedEmployee(employee);
    setFormData((prev) => ({ ...prev, employe_id: employeeId }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    if (
      !formData.employe_id ||
      !formData.periode_debut ||
      !formData.periode_fin ||
      !formData.salaire_base
    ) {
      showError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      setLoading(true);

      const payrollData = {
        employe_id: formData.employe_id,
        periode_debut: formData.periode_debut,
        periode_fin: formData.periode_fin,
        salaire_base: parseFloat(formData.salaire_base),
        heures_travaillees: parseFloat(formData.heures_travaillees),
        heures_supplementaires: parseFloat(formData.heures_supplementaires),
        taux_heures_sup: parseFloat(formData.taux_heures_sup),
        primes: parseFloat(formData.primes),
        nombre_personnes_charge: parseInt(formData.nombre_personnes_charge),
        autres_deductions: parseFloat(formData.autres_deductions),
        // Calculated fields
        salaire_brut: calculations.salaire_brut,
        cotisations_cnss: calculations.cotisations_cnss,
        cotisations_amo: calculations.cotisations_amo,
        cotisations_retraite: calculations.cotisations_retraite,
        cotisations_sociales: calculations.cotisations_sociales,
        frais_professionnels: calculations.frais_professionnels,
        salaire_net_imposable: calculations.salaire_net_imposable,
        ir_brut: calculations.ir_brut,
        deduction_personnes: calculations.deduction_personnes,
        ir_net: calculations.ir_net,
        salaire_net: calculations.salaire_net,
      };

      const response = await payrollAPI.calculatePayroll(payrollData);

      console.log("API Response:", response);
      console.log("Response data:", response.data);

      if (response.data.success) {
        showSuccess("Bulletin de paie marocain généré avec succès !");
        onGenerated(response.data.data);
        onClose();
      } else {
        showError(response.data.message || "Erreur lors de la génération");
      }
    } catch (error) {
      console.error("Error generating payslip:", error);
      showError("Erreur lors de la génération du bulletin de paie");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleGenerate} className="space-y-6">
      {/* Employee Selection */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <User className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium">Employé</h3>
        </div>

        <div>
          <Label htmlFor="employe_id">Sélectionner un employé</Label>
          <Select
            value={formData.employe_id}
            onValueChange={handleEmployeeChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner un employé" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((employee) => (
                <SelectItem key={employee.id} value={employee.id}>
                  <div className="flex flex-col">
                    <span>
                      {employee.prenom} {employee.nom}
                    </span>
                    <span className="text-sm text-gray-500">
                      {employee.departement?.nom} - {employee.email}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedEmployee && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Details de l'employé</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Département:</span>
                <span className="ml-2">
                  {selectedEmployee.departement?.nom}
                </span>
              </div>
              <div>
                <span className="text-gray-600">
                  {t("employees.position")}:
                </span>
                <span className="ml-2">{selectedEmployee.type_contrat}</span>
              </div>
              <div>
                <span className="text-gray-600">
                  {t("payroll.currentSalary")}:
                </span>
                <span className="ml-2">
                  {parseFloat(selectedEmployee.salaire || 0).toLocaleString(
                    "fr-FR",
                    {
                      style: "currency",
                      currency: "MAD",
                    }
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Period */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium">Période de paie</h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="periode_debut">Début de période</Label>
            <Input
              id="periode_debut"
              type="date"
              value={formData.periode_debut}
              onChange={(e) =>
                handleInputChange("periode_debut", e.target.value)
              }
              required
            />
          </div>
          <div>
            <Label htmlFor="periode_fin">Fin de période</Label>
            <Input
              id="periode_fin"
              type="date"
              value={formData.periode_fin}
              onChange={(e) => handleInputChange("periode_fin", e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Salary Details */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <DollarSign className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium">Détails du salaire</h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="salaire_base">Salaire de base</Label>
            <Input
              id="salaire_base"
              type="number"
              step="0.01"
              value={formData.salaire_base}
              onChange={(e) =>
                handleInputChange("salaire_base", e.target.value)
              }
              required
            />
          </div>
          <div>
            <Label htmlFor="primes">Primes</Label>
            <Input
              id="primes"
              type="number"
              step="0.01"
              value={formData.primes}
              onChange={(e) => handleInputChange("primes", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="heures_supplementaires">
              Heures supplémentaires
            </Label>
            <Input
              id="heures_supplementaires"
              type="number"
              step="0.5"
              value={formData.heures_supplementaires}
              onChange={(e) =>
                handleInputChange("heures_supplementaires", e.target.value)
              }
            />
          </div>
          <div>
            <Label htmlFor="taux_heures_sup">Taux heures sup.</Label>
            <Input
              id="taux_heures_sup"
              type="number"
              step="0.01"
              value={formData.taux_heures_sup}
              onChange={(e) =>
                handleInputChange("taux_heures_sup", e.target.value)
              }
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Calculations Preview */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Calculs</h3>

        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex justify-between">
            <span>Salaire de base</span>
            <span>
              {parseFloat(formData.salaire_base || 0).toLocaleString("fr-FR", {
                style: "currency",
                currency: "MAD",
              })}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Paie heures sup.</span>
            <span>
              {(
                parseFloat(formData.heures_supplementaires || 0) *
                (parseFloat(formData.salaire_base || 0) / 173.33) *
                parseFloat(formData.taux_heures_sup || 1.25)
              ).toLocaleString("fr-FR", { style: "currency", currency: "MAD" })}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Primes</span>
            <span>
              {parseFloat(formData.primes || 0).toLocaleString("fr-FR", {
                style: "currency",
                currency: "MAD",
              })}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between font-semibold">
            <span>Salaire brut</span>
            <span>
              {calculations.salaire_brut.toLocaleString("fr-FR", {
                style: "currency",
                currency: "MAD",
              })}
            </span>
          </div>
          <div className="flex justify-between text-red-600">
            <span>Cotisations sociales</span>
            <span>
              -
              {calculations.cotisations_sociales.toLocaleString("fr-FR", {
                style: "currency",
                currency: "MAD",
              })}
            </span>
          </div>
          <div className="flex justify-between text-red-600">
            <span>Impôts</span>
            <span>
              -
              {calculations.ir_net.toLocaleString("fr-FR", {
                style: "currency",
                currency: "MAD",
              })}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between font-bold text-lg text-green-600">
            <span>Salaire net</span>
            <span>
              {calculations.salaire_net.toLocaleString("fr-FR", {
                style: "currency",
                currency: "MAD",
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Chargement...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Save className="h-4 w-4" />
              <span>Générer le bulletin</span>
            </div>
          )}
        </Button>
      </div>
    </form>
  );
}
